/* eslint-disable @typescript-eslint/no-explicit-any */
import { MouseEvent, useMemo, useState } from 'react';
import {
    Button,
    Field,
    makeStyles,
    mergeClasses,
    Text,
    tokens,
    Toolbar,
    ToolbarDivider,
    ToolbarRadioButton,
    ToolbarRadioGroup,
    Tooltip,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
    AddCircle16Regular,
    CalendarMultipleRegular,
    CalendarRegular,
    ChevronLeft16Regular,
    ChevronRight16Regular,
    DeleteRegular,
    EditRegular,
    Note20Regular,
    TableRegular,
} from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { Column, BrandCard, Row } from '../../../common';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { DateControl } from '../../../controls/CaseEventControl';
import { AddEditCaseEventDialog } from '../../../dialogs/AddEditCaseEventDialog';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { useToasts } from '../../../toasts/useToasts';
import {
    CustomDataTable,
    CustomDataTableAction,
    CustomDataTableColumn,
    CustomDataTableProps,
    CustomTableCellLayout,
} from '../../../controls/CustomDataTable/CustomDataTable';
import { Case, CaseEvent } from '../../../../data/types';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { useCustomDataTableFeatures } from '../../../controls/CustomDataTable/useCustomDataTableFeatures';
import { appTokens } from '../../../common/Styles/appTokens';
import { DropdownMenu } from '../../../controls/Menus/DropdownMenu';
import { useUtilities } from '../../../../utilities/useUtilities';
import { CaseNoteService } from '../../../../data/services';
import { Note } from '@react-pdf/renderer';
import { CaseTaskNotesDialog } from '../CaseTasks/CaseTaskNotesDialog';

enum ColumnName {
    date = 'date',
    eventName = 'eventName',
    description = 'description',
    actions = 'actions',
    notes = 'notes',
}

enum ViewMode {
    table = 'table',
    calendar = 'calendar',
}

type CalendarDay = {
    date: Date;
    inCurrentMonth: boolean;
};

const useStyles = makeStyles({
    dateControl: {
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        padding: '.5 rem',
        width: '3rem',
        borderRadius: '10px',
    },
    calendarMonthLabel: {
        fontWeight: '600',
    },
    monthPickerField: {
        minWidth: '14rem',
    },
    calendarGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gridAutoRows: 'minmax(9.25rem, 1fr)',
        gap: '.25rem',
        overflowY: 'auto',
        paddingInline: '.5rem',
        paddingBlockEnd: '.75rem',
    },
    dayCell: {
        border: `2px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '8px',
        backgroundColor: tokens.colorNeutralBackground1,
        minWidth: 0,
    },
    outsideMonthDayCell: {
        backgroundColor: tokens.colorNeutralBackground3,
        opacity: 0.7,
    },
    todayDayCell: {
        border: `2px solid ${tokens.colorBrandStroke1}`,
    },
    dayNumber: {
        fontSize: '12px',
        fontWeight: 600,
    },
    outsideMonthDayNumber: {
        color: tokens.colorNeutralForeground3,
    },
    dayEvents: {
        display: 'flex',
        flexDirection: 'column',
        gap: '.15rem',
        overflow: 'hidden',
    },
    moreEventsText: {
        color: tokens.colorNeutralForeground3,
        fontSize: '11px',
    },
    event: {
        cursor: 'pointer',
    },
    eventText: {
        color: tokens.colorBrandForeground2,
    },
});

const normalizeToDateOnly = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const tryGetDate = (date?: Date | string) => {
    if (!date) {
        return undefined;
    }

    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const hasNonMidnightTime = (date?: Date | string) => {
    const parsed = tryGetDate(date);

    if (!parsed) {
        return false;
    }

    return (
        parsed.getHours() !== 0 || parsed.getMinutes() !== 0 || parsed.getSeconds() !== 0 || parsed.getMilliseconds() !== 0
    );
};

const getEventRange = (event: CaseEvent) => {
    const startRaw = event.date;
    const endRaw = event.endDateTime;

    const startDate = tryGetDate(startRaw);
    const endDate = tryGetDate(endRaw) ?? startDate;

    if (!startDate) {
        return undefined;
    }

    const normalizedStart = normalizeToDateOnly(startDate);
    const normalizedEnd = normalizeToDateOnly(endDate || startDate);

    if (normalizedEnd < normalizedStart) {
        return { startDate: normalizedEnd, endDate: normalizedStart };
    }

    return { startDate: normalizedStart, endDate: normalizedEnd };
};

const getCalendarDays = (monthDate: Date): CalendarDay[] => {
    const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay());

    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + index);
        return {
            date,
            inCurrentMonth: date.getMonth() === monthDate.getMonth() && date.getFullYear() === monthDate.getFullYear(),
        };
    });
};

const isDateInRange = (date: Date, startDate: Date, endDate: Date) => date >= startDate && date <= endDate;

type CaseEventsCalendarViewProps = {
    monthDate: Date;
    events: CaseEvent[];
    onMonthBack: () => void;
    onMonthForward: () => void;
    onMonthPicked: (date: Date | null | undefined) => void;
    submitEvent?: (event: CaseEvent | any) => Promise<void>;
};

const CaseEventsCalendarView = (props: CaseEventsCalendarViewProps) => {
    const { monthDate, events, onMonthBack, onMonthForward, onMonthPicked, submitEvent } = props;

    //Hook
    const utils = useUtilities();

    //State
    const [actionEvent, setActionEvent] = useState<CaseEvent>();
    const [editOpen, setEditOpen] = useState<boolean>(false);

    const styles = useStyles();
    const today = normalizeToDateOnly(new Date());
    const days = useMemo(() => getCalendarDays(monthDate), [monthDate]);

    const eventRanges = useMemo(
        () =>
            events
                .map((event) => {
                    const range = getEventRange(event);
                    return range ? { event, ...range } : undefined;
                })
                .filter((event): event is { event: CaseEvent; startDate: Date; endDate: Date } => !!event)
                .sort((a, b) => a.startDate.getTime() - b.startDate.getTime()),
        [events],
    );

    const monthLabel = monthDate.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });

    const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const onEditOpenChange = (isOpen: boolean) => {
        setEditOpen(isOpen as boolean);
        //setActionEvent(undefined);
    };
    const onEventClick = (event: CaseEvent) => {
        setActionEvent(event);
        setEditOpen(true);
    };
    const getEventTimes = (event: CaseEvent) => {
        const hasStartTime = hasNonMidnightTime(event.date);
        const hasEndTime = hasNonMidnightTime(event.endDateTime);
        const startTime = hasStartTime ? utils.date.formatDate(event.date, { hour12: true, minute: '2-digit' }) : '';
        const endTime = hasEndTime ? utils.date.formatDate(event.endDateTime, { hour12: true, minute: '2-digit' }) : '';

        if (event.allDay || !startTime) return '';
        return `${startTime}${!!endTime && !event.due ? ` - ${endTime}` : ''}`;
    };

    return (
        <Column gap='medium' flex='1' maxHeight={appTokens.pageHeight('15.5rem')} padding='px-2'>
            <Row>
                <Column vAlign='middle' padding='pl-2'>
                    <Text size={400} className={styles.calendarMonthLabel}>
                        {monthLabel}
                    </Text>
                </Column>
                <Column width='auto' vAlign='bottom'>
                    <Row width='auto' gap='medium'>
                        <Button
                            size='small'
                            icon={<ChevronLeft16Regular />}
                            appearance='secondary'
                            aria-label='Previous month'
                            onClick={onMonthBack}
                            data-testid='case-events-calendar-previous-month-btn'
                        />
                        <Button
                            size='small'
                            icon={<ChevronRight16Regular />}
                            appearance='secondary'
                            aria-label='Next month'
                            onClick={onMonthForward}
                            data-testid='case-events-calendar-next-month-btn'
                        />
                    </Row>
                </Column>
                <Column width='auto' vAlign='middle'>
                    <Field className={styles.monthPickerField} label='Jump to month' size='small'>
                        <DatePicker
                            value={monthDate}
                            onSelectDate={onMonthPicked}
                            formatDate={(date) =>
                                date
                                    ? date.toLocaleDateString('en-US', {
                                          month: 'long',
                                          year: 'numeric',
                                      })
                                    : ''
                            }
                            data-testid='case-events-calendar-month-picker'
                        />
                    </Field>
                </Column>
            </Row>

            <Row gap='small' padding='px-2' justifyContent='space-between'>
                {weekdayLabels.map((dayLabel) => (
                    <Column key={dayLabel} align='center' vAlign='middle'>
                        <Text weight='semibold' size={200}>
                            {dayLabel}
                        </Text>
                    </Column>
                ))}
            </Row>

            <div className={styles.calendarGrid}>
                {days.map((day) => {
                    const dateOnly = normalizeToDateOnly(day.date);
                    const dayEvents = eventRanges.filter((eventRange) =>
                        isDateInRange(dateOnly, eventRange.startDate, eventRange.endDate),
                    );
                    const visibleEvents = dayEvents.slice(0, 3);
                    const hiddenCount = dayEvents.length - visibleEvents.length;
                    const isToday = dateOnly.getTime() === today.getTime();

                    const dayCellClasses = mergeClasses(
                        styles.dayCell,
                        !day.inCurrentMonth && styles.outsideMonthDayCell,
                        isToday && styles.todayDayCell,
                    );

                    return (
                        <Column
                            key={dateOnly.toISOString()}
                            className={dayCellClasses}
                            gap='small'
                            padding='px-2 pb-1'
                            data-testid={`case-events-calendar-day-${dateOnly.toISOString().split('T')[0]}`}
                        >
                            <Text
                                size={200}
                                weight='semibold'
                                className={` ${!day.inCurrentMonth ? styles.outsideMonthDayNumber : ''}`}
                            >
                                {dateOnly.getDate()}
                            </Text>
                            <Column gap='small' scroll padding='pb-1'>
                                {visibleEvents.map((eventRange) => (
                                    <>
                                        <Column
                                            gap='small'
                                            padding='px-2 py-1'
                                            border
                                            borderRadius='10px'
                                            backgroundColor={tokens.colorBrandBackground2}
                                            className={styles.event}
                                            onClick={() => onEventClick(eventRange.event)}
                                        >
                                            <Text size={200} className={styles.eventText}>
                                                {eventRange.event.event}
                                            </Text>
                                            <Text size={200} className={styles.eventText}>
                                                {getEventTimes(eventRange.event)}
                                            </Text>
                                        </Column>
                                    </>
                                ))}
                                {hiddenCount > 0 && <Text className={styles.moreEventsText}>+{hiddenCount} more</Text>}
                            </Column>
                        </Column>
                    );
                })}
            </div>
            <AddEditCaseEventDialog
                onSubmit={submitEvent}
                eventToEdit={actionEvent}
                caseId={actionEvent?.caseId as string}
                open={editOpen}
                onOpenChange={onEditOpenChange}
            />
        </Column>
    );
};

type CaseEventsTableProps = Partial<CustomDataTableProps<CaseEvent>> & {
    events: CaseEvent[];
    noteService?: CaseNoteService;
    submitEvent?: (event: CaseEvent | any) => Promise<void>;
    deleteEvent?: (event: CaseEvent) => void;
    bodyHeight?: string;
    onSelectionChanged?: (events: CaseEvent[]) => void;
};
export const CaseEventsTable = (props: CaseEventsTableProps) => {
    const { events = [], submitEvent, deleteEvent, bodyHeight, onSelectionChanged, noteService, ...otherProps } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);

    //State
    const [delConfirmOpen, setDelConfirmOpen] = useState<boolean>(false);
    const [editOpen, setEditOpen] = useState<boolean>(false);
    const [actionEvent, setActionEvent] = useState<CaseEvent>();
    const [notesDialogOpen, setNotesDialogOpen] = useState<boolean>(false);
    const [actionEventNotes, setActionEventNotes] = useState<Note[]>();

    //Styles
    const styles = useStyles();

    //Logic
    const clickDelete = (event: CaseEvent) => {
        !!deleteEvent && deleteEvent(event);
    };
    const onDeleteConfirm = (ev?: MouseEvent) => {
        ev?.stopPropagation();
        if (actionEvent) clickDelete(actionEvent);
    };
    const onDeleteConfirmOpenChange = (isOpen: any) => {
        setDelConfirmOpen(isOpen as boolean);
        //setActionEvent(undefined);
    };
    const onEditOpenChange = (isOpen: boolean) => {
        setEditOpen(isOpen as boolean);
        //setActionEvent(undefined);
    };

    //Columns and Actions
    const columns: CustomDataTableColumn<CaseEvent>[] = [
        {
            id: ColumnName.date,
            compare: (a, b) => {
                const aDate = !!a.date ? new Date(a.date).getTime() : 0;
                const bDate = !!b.date ? new Date(b.date).getTime() : 0;
                return aDate - bDate;
            },
            header: 'Date',
            renderCell: (event) => {
                return (
                    <>
                        {!!event?.date && (
                            <DateControl
                                className={styles.dateControl}
                                date={new Date(event?.date)}
                                due={event?.due}
                                dueStamp
                                billable={event?.billable}
                            />
                        )}
                    </>
                );
            },
            defaultWidth: 100,
            minWidth: 100,
        },
        {
            id: ColumnName.eventName,
            compare: (a, b) => {
                return a.event?.localeCompare(b.event || '') || 0;
            },
            header: 'Event',
            renderCell: (event) => {
                return (
                    <>
                        <Text>{event?.event}</Text>
                    </>
                );
            },
            defaultWidth: 300,
            minWidth: 100,
        },
        {
            id: ColumnName.description,
            compare: (a, b) => {
                return a.description?.localeCompare(b.description || '') || 0;
            },
            header: 'Description',
            renderCell: (event) => {
                return (
                    <>
                        <Text>{event?.description}</Text>
                    </>
                );
            },
            defaultWidth: 550,
            minWidth: 200,
        },
        {
            id: ColumnName.notes,
            header: 'Notes',
            renderCell: (event) => {
                const eventNotes = noteService?.notes.filter((note) => note.eventId == event.eventId) || [];
                const notesCount = eventNotes.length;
                return (
                    <>
                        {!!notesCount && (
                            <Tooltip content={`${notesCount} ${notesCount === 1 ? 'Note' : 'Notes'}`} relationship='label'>
                                <span
                                    onClick={() => {
                                        setActionEvent(event);
                                        setActionEventNotes(eventNotes);
                                        setNotesDialogOpen(true);
                                    }}
                                >
                                    <Note20Regular />
                                </span>
                            </Tooltip>
                        )}
                    </>
                );
            },
            justifyContent: 'center',
            defaultWidth: 50,
            minWidth: 50,
            pointerEvents: true,
        },
    ];

    const actions: CustomDataTableAction<CaseEvent>[] = [
        {
            id: 'edit',
            ariaLabel: 'Edit',
            canAccess: (event) => access.cases.canEditEvents(event?.caseId) || false,
            icon: <EditRegular />,
            label: 'Edit',
            tooltip: 'Edit',
            singleSelectItemOnClick: true,
            onClick: (event) => {
                setActionEvent(event);
                setEditOpen(true);
            },
            onMenuClick: (event) => {
                setActionEvent(event);
                setEditOpen(true);
            },
        },
        {
            id: 'delete',
            ariaLabel: 'Delete',
            canAccess: (event) => access.cases.canDeleteEvents(event?.caseId) || false,
            icon: <DeleteRegular />,
            label: 'Delete',
            tooltip: 'Delete',
            singleSelectItemOnClick: true,
            onMenuClick: (event) => {
                setActionEvent(event);
                setDelConfirmOpen(true);
            },
            onClick: (event) => {
                setActionEvent(event);
                setDelConfirmOpen(true);
            },
        },
    ];

    return (
        <>
            <CustomDataTable<CaseEvent>
                data-testid='case-events-table'
                bodyHeight={bodyHeight}
                items={events || []}
                rowId='eventId'
                columns={columns}
                actions={actions}
                select={
                    access.cases.canEditEvents(events[0]?.caseId) || access.cases.canDeleteEvents(events[0]?.caseId) || false
                }
                onSelectionChanged={onSelectionChanged}
                onDoubleClickItem={(event) => {
                    setActionEvent(event);
                    setEditOpen(true);
                }}
                {...otherProps}
            />
            <ConfirmationDialog
                title='Confirm Delete'
                icon={<DeleteRegular />}
                text={`Are you sure you want to delete this event?`}
                onConfirm={onDeleteConfirm}
                open={delConfirmOpen}
                onOpenChange={onDeleteConfirmOpenChange}
            />
            <AddEditCaseEventDialog
                onSubmit={submitEvent}
                eventToEdit={actionEvent}
                caseId={actionEvent?.caseId as string}
                open={editOpen}
                onOpenChange={onEditOpenChange}
            />
            <CaseTaskNotesDialog
                title={`${actionEvent?.event || 'Event Notes'}`}
                notes={actionEventNotes}
                open={notesDialogOpen}
                onOpenChange={(isOpen) => {
                    setNotesDialogOpen(isOpen as boolean);
                    if (!isOpen) {
                        // setActionTask(undefined);
                        // setActionTaskNotes([]);
                    }
                }}
            />
        </>
    );
};

enum MyTabs {
    all = 'all',
    current = 'current',
    past = 'past',
}

export const CaseEventsPage = () => {
    const { caseForm, eventService, noteService } = useCaseServicesContext()!;

    //Vars
    const caseObj = caseForm.values as Case;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const toasts = useToasts();
    const tableFeatures = useCustomDataTableFeatures();

    //State
    const [selectedTab, setSelectedTab] = useState<string>(MyTabs.all);
    const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.table);
    const [selectedEvents, setSelectedEvents] = useState<CaseEvent[]>([]);
    const [viewedMonth, setViewedMonth] = useState<Date>(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });

    //Styles
    const commonStyles = useCommonStyles();

    //Logic
    const clearSelection = () => {
        setSelectedEvents([]);
        tableFeatures.clearSelection();
    };
    const onTabSelect = (option: string) => {
        clearSelection();
        setSelectedTab(option);
    };
    const onViewModeChanged = (option: string) => {
        clearSelection();
        setViewMode(option === ViewMode.calendar ? ViewMode.calendar : ViewMode.table);
    };
    const filterEvents = (event: CaseEvent) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (selectedTab == MyTabs.past) {
            return new Date(event.date as Date) < today;
        } else if (selectedTab == MyTabs.current) {
            return new Date(event.date as Date) >= today;
        } else {
            return true;
        }
    };
    const submitCaseEvent = async (caseEvent: CaseEvent) => {
        if (caseEvent.event) {
            await eventService.upsertCaseEventAsync(caseEvent, true);
        }
    };
    const deleteCaseEvent = async (event: CaseEvent) => {
        try {
            await eventService.deleteCaseEventAsync(event.eventId as string);
            toasts.caseEvents.notifyCaseEventDeleted(event);

            clearSelection();
        } catch {
            toasts.caseEvents.notifyCaseEventDeleteError(event);
        }
    };
    const onSelectedEventsChanged = (events: CaseEvent[]) => {
        setSelectedEvents(events);
    };
    const deleteEvents = async (events: CaseEvent[]) => {
        await eventService.deleteCaseEventsAsync(events.map((x) => x.eventId as string));
        clearSelection();
    };
    const onDeleteSelectedEventsConfirm = (ev?: MouseEvent) => {
        ev?.stopPropagation();
        void deleteEvents(selectedEvents);
    };
    const onMonthBack = () => {
        setViewedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };
    const onMonthForward = () => {
        setViewedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };
    const onMonthPicked = (date: Date | null | undefined) => {
        if (!date) {
            return;
        }

        setViewedMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    };

    const filteredEvents = eventService.events.filter(filterEvents);

    return (
        <Column flex='1' padding='px-2 py-1'>
            <BrandCard
                noGap
                flex='1'
                title='Events'
                headerIcon={<CalendarMultipleRegular />}
                headerComponent={
                    <Column flex='1' align='right'>
                        <Row width='auto' padding='pr-4' gap='small'>
                            <Column width='auto' vAlign='middle'>
                                <Toolbar style={{ minHeight: '2rem' }}>
                                    {!!selectedEvents?.length && viewMode === ViewMode.table && (
                                        <>
                                            <Text data-testid='case-events-page-selected-count'>
                                                {selectedEvents.length} expense
                                                {selectedEvents.length > 1 ? 's' : ''} selected
                                            </Text>

                                            <ToolbarDivider />

                                            {!!access.cases.canDeleteEvents(caseObj?.caseId) && (
                                                <ConfirmationDialog
                                                    title='Confirm Delete'
                                                    icon={<DeleteRegular />}
                                                    text={`Are you sure you want to delete the selected events?`}
                                                    onConfirm={onDeleteSelectedEventsConfirm}
                                                >
                                                    <Tooltip content='Delete Selected Events' relationship='description'>
                                                        <Button
                                                            className={commonStyles.button.root}
                                                            aria-label='Delete'
                                                            icon={<DeleteRegular />}
                                                            appearance='subtle'
                                                            shape='circular'
                                                            data-testid='case-events-page-delete-selected-btn'
                                                        ></Button>
                                                    </Tooltip>
                                                </ConfirmationDialog>
                                            )}
                                        </>
                                    )}
                                </Toolbar>
                            </Column>
                            {viewMode === ViewMode.table && (
                                <Column width='auto' vAlign='middle' >
                                    <DropdownMenu
                                        options={[MyTabs.all, MyTabs.current, MyTabs.past].map((option) => ({
                                            label:
                                                option == MyTabs.current
                                                    ? 'Current Events'
                                                    : MyTabs.all == option
                                                      ? 'All Events'
                                                      : 'Past Events',
                                            value: option,
                                        }))}
                                        title='Time Filter'
                                        size='medium'
                                        initialValue={selectedTab}
                                        onOptionChanged={onTabSelect}
                                        width='fit-content'
                                    />
                                </Column>
                            )}
                            <Column width='auto' vAlign='top' align='left' padding='pl-3' noGap>
                                <Row height='auto' width='auto' gap='small'>
                                    <Column vAlign='middle' align='center' noGap>
                                        <Text size={300}>View Mode</Text>
                                    </Column>
                                    <Column width='auto' align='center' vAlign='middle' noGap>
                                        <Toolbar
                                            size='medium'
                                            defaultCheckedValues={{
                                                viewMode: [ViewMode.table],
                                            }}
                                            aria-label='View Mode'
                                        >
                                            <ToolbarRadioGroup>
                                                <Tooltip content='Table View' relationship='description'>
                                                    <ToolbarRadioButton
                                                        name='viewMode'
                                                        aria-label='Table View'
                                                        value={ViewMode.table}
                                                        icon={<TableRegular />}
                                                        onClick={() => onViewModeChanged(ViewMode.table)}
                                                    />
                                                </Tooltip>
                                                <Tooltip content='Calendar View' relationship='description'>
                                                    <ToolbarRadioButton
                                                        name='viewMode'
                                                        aria-label='Calendar View'
                                                        value={ViewMode.calendar}
                                                        icon={<CalendarRegular />}
                                                        onClick={() => onViewModeChanged(ViewMode.calendar)}
                                                    />
                                                </Tooltip>
                                            </ToolbarRadioGroup>
                                        </Toolbar>
                                    </Column>
                                </Row>
                            </Column>
                        </Row>
                    </Column>
                }
                action={
                    <Column width='auto' vAlign='middle'>
                        {!!access.cases.canAddEvents(caseObj.caseId) && (
                            <AddEditCaseEventDialog onSubmit={submitCaseEvent} caseId={caseObj.caseId!}>
                                <Button
                                    className={commonStyles.button.root}
                                    icon={<AddCircle16Regular />}
                                    appearance='secondary'
                                    data-testid='case-events-page-add-event-btn'
                                >
                                    Add Event
                                </Button>
                            </AddEditCaseEventDialog>
                        )}
                    </Column>
                }
            >
                <Column flex='1'>
                    {viewMode === ViewMode.table ? (
                        <CaseEventsTable
                            events={filteredEvents}
                            bodyHeight={appTokens.pageHeight('17.5rem')}
                            noteService={noteService}
                            submitEvent={submitCaseEvent}
                            deleteEvent={deleteCaseEvent}
                            onSelectionChanged={onSelectedEventsChanged}
                            {...tableFeatures.tableProps}
                        />
                    ) : (
                        <CaseEventsCalendarView
                            events={eventService.events}
                            monthDate={viewedMonth}
                            onMonthBack={onMonthBack}
                            onMonthForward={onMonthForward}
                            onMonthPicked={onMonthPicked}
                            submitEvent={submitCaseEvent}
                        />
                    )}
                </Column>
            </BrandCard>
        </Column>
    );
};
